import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';

const EU_COUNTRIES = [
  { code: 'DE', name: 'Niemcy' }, { code: 'FR', name: 'Francja' },
  { code: 'NL', name: 'Holandia' }, { code: 'BE', name: 'Belgia' },
  { code: 'AT', name: 'Austria' }, { code: 'IT', name: 'Włochy' },
  { code: 'ES', name: 'Hiszpania' }, { code: 'CZ', name: 'Czechy' },
  { code: 'SK', name: 'Słowacja' }, { code: 'SE', name: 'Szwecja' },
  { code: 'DK', name: 'Dania' }, { code: 'FI', name: 'Finlandia' },
  { code: 'IE', name: 'Irlandia' }, { code: 'PT', name: 'Portugalia' },
  { code: 'LU', name: 'Luksemburg' }, { code: 'LT', name: 'Litwa' },
  { code: 'LV', name: 'Łotwa' }, { code: 'EE', name: 'Estonia' },
  { code: 'HR', name: 'Chorwacja' }, { code: 'SI', name: 'Słowenia' },
  { code: 'BG', name: 'Bułgaria' }, { code: 'RO', name: 'Rumunia' },
  { code: 'GR', name: 'Grecja' }, { code: 'HU', name: 'Węgry' },
  { code: 'MT', name: 'Malta' }, { code: 'CY', name: 'Cypr' },
  { code: 'NO', name: 'Norwegia' }, { code: 'IS', name: 'Islandia' },
  { code: 'LI', name: 'Liechtenstein' }, { code: 'CH', name: 'Szwajcaria' },
  { code: 'GB', name: 'Wielka Brytania' },
];

const STATUS_COLORS = {
  draft: '#6b7280', submitted: '#3b82f6', approved: '#10b981', rejected: '#ef4444',
};
const STATUS_LABELS = {
  draft: 'Szkic', submitted: 'Złożony', approved: 'Zatwierdzony', rejected: 'Odrzucony',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, opacity: 0.7, marginBottom: 4, display: 'block' };
const fieldGroup = { marginBottom: 14 };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

export default function A1Application() {
  const { currentCompany, setPage } = useApp();
  const [view, setView] = useState('list'); // list | form | detail
  const [apps, setApps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const cid = currentCompany?.id;

  useEffect(() => { if (cid) loadData(); }, [cid]);

  async function loadData() {
    setLoading(true);
    try {
      const [aRes, eRes] = await Promise.all([
        api.get(`/api/a1/applications?company_id=${cid}`),
        api.get(`/hr/employees?company_id=${cid}`).catch(() => ({ data: [] })),
      ]);
      setApps(aRes.data);
      setEmployees(eRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function startNew() {
    setEditId(null);
    setForm({
      company_id: cid,
      employee_id: '',
      first_name: '', last_name: '', pesel: '', date_of_birth: '',
      place_of_birth: '', nationality: '', passport_number: '',
      social_security_number: '', full_address: '',
      employment_status: 'employed',
      employer_zus_nkp: '',
      destination_country: '', destination_company_name: '', destination_company_address: '',
      posting_start_date: '', posting_end_date: '',
      posting_type: 'art12', type_of_work: '',
      previous_a1: false, replaces_worker: false,
      notes: '',
    });
    setView('form');
  }

  function openEdit(a) {
    setEditId(a.id);
    setForm({ ...a, company_id: cid });
    setView('form');
  }

  function onEmployeeSelect(empId) {
    const emp = employees.find(e => e.id === Number(empId));
    if (!emp) return;
    setForm(f => ({
      ...f, employee_id: emp.id,
      first_name: emp.first_name || '', last_name: emp.last_name || '',
      pesel: emp.pesel || '', date_of_birth: emp.date_of_birth || '',
      nationality: emp.citizenship || '', passport_number: emp.passport_number || '',
      social_security_number: emp.pesel || '',
      full_address: [emp.address_street, emp.address_city].filter(Boolean).join(', '),
    }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form, company_id: cid };
      if (editId) {
        await api.put(`/api/a1/applications/${editId}`, payload);
      } else {
        await api.post('/api/a1/applications', payload);
      }
      await loadData();
      setView('list');
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd zapisu');
    }
    setSaving(false);
  }

  async function deleteApp(id) {
    if (!confirm('Usunąć wniosek?')) return;
    try {
      await api.delete(`/api/a1/applications/${id}`);
      await loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Błąd'); }
  }

  async function submitToZus(id) {
    // Update status to submitted + download XML
    try {
      await api.put(`/api/a1/applications/${id}`, { status: 'submitted' });
      await loadData();
      downloadXml(id);
    } catch (e) { alert('Błąd'); }
  }

  function downloadPdf(id) {
    window.open(`${api.defaults.baseURL}/api/a1/applications/${id}/pdf`, '_blank');
  }
  function downloadXml(id) {
    window.open(`${api.defaults.baseURL}/api/a1/applications/${id}/xml`, '_blank');
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── List View ──
  if (view === 'list') return (
    <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>🇪🇺 Zaświadczenie A1</div>
          <div style={{ fontSize: 12, opacity: 0.5 }}>Delegowanie pracowników — Rozporządzenie 883/2004</div>
        </div>
        <button onClick={startNew} style={{
          padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 600,
        }}>+ Nowy wniosek</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>Ładowanie...</div> : (
        apps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, opacity: 0.4 }}>
            <div style={{ fontSize: 48 }}>📋</div>
            <div style={{ marginTop: 8 }}>Brak wniosków A1</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {apps.map(a => (
              <div key={a.id} style={{
                padding: 16, borderRadius: 12, cursor: 'pointer',
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{a.first_name} {a.last_name}</div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>
                      → {EU_COUNTRIES.find(c => c.code === a.destination_country)?.name || a.destination_country}
                      {a.destination_company_name ? ` • ${a.destination_company_name}` : ''}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>
                      {a.posting_start_date} — {a.posting_end_date} • {a.posting_type === 'art12' ? 'Art. 12' : 'Art. 13'}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: `${STATUS_COLORS[a.status]}22`, color: STATUS_COLORS[a.status],
                  }}>{STATUS_LABELS[a.status] || a.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {a.status === 'draft' && (
                    <button onClick={() => openEdit(a)} style={btnSmall}>✏️ Edytuj</button>
                  )}
                  <button onClick={() => downloadPdf(a.id)} style={btnSmall}>📄 PDF</button>
                  <button onClick={() => downloadXml(a.id)} style={btnSmall}>📥 XML (KEDU)</button>
                  {a.status === 'draft' && (
                    <button onClick={() => submitToZus(a.id)} style={{ ...btnSmall, background: 'rgba(16,185,129,0.2)', color: '#10b981' }}>
                      🏛 Złóż do ZUS
                    </button>
                  )}
                  {a.status === 'draft' && (
                    <button onClick={() => deleteApp(a.id)} style={{ ...btnSmall, background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>🗑</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Info box */}
      <div style={{
        marginTop: 20, padding: 14, borderRadius: 12, fontSize: 12, opacity: 0.6,
        background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)',
      }}>
        <strong>ℹ️ Informacja:</strong> Plik XML (KEDU) należy zaimportować w PUE ZUS → ePłatnik → Import dokumentów.
        Bezpośrednia wysyłka przez API ZUS wymaga podpisu kwalifikowanego (planowana w przyszłej wersji).
      </div>
    </div>
  );

  // ── Form View ──
  return (
    <div style={{ padding: 16, maxWidth: 700, margin: '0 auto' }}>
      <button onClick={() => setView('list')} style={{
        background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: 14, marginBottom: 12,
      }}>← Powrót do listy</button>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
        {editId ? 'Edytuj wniosek A1' : 'Nowy wniosek A1'}
      </h2>

      {/* Section 1: Employee */}
      <SectionTitle num={1} title="DANE OSOBOWE PRACOWNIKA" />
      {!editId && (
        <div style={fieldGroup}>
          <label style={labelStyle}>Wybierz pracownika</label>
          <select value={form.employee_id} onChange={e => onEmployeeSelect(e.target.value)} style={inputStyle}>
            <option value="">— Wybierz —</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} {emp.pesel ? `(${emp.pesel})` : ''}</option>
            ))}
          </select>
        </div>
      )}
      <div style={row2}>
        <Field label="Imię" value={form.first_name} onChange={v => set('first_name', v)} />
        <Field label="Nazwisko" value={form.last_name} onChange={v => set('last_name', v)} />
      </div>
      <div style={row2}>
        <Field label="PESEL" value={form.pesel} onChange={v => set('pesel', v)} />
        <Field label="Data urodzenia" type="date" value={form.date_of_birth} onChange={v => set('date_of_birth', v)} />
      </div>
      <div style={row2}>
        <Field label="Miejsce urodzenia" value={form.place_of_birth} onChange={v => set('place_of_birth', v)} />
        <Field label="Obywatelstwo" value={form.nationality} onChange={v => set('nationality', v)} />
      </div>
      <div style={row2}>
        <Field label="Nr paszportu" value={form.passport_number} onChange={v => set('passport_number', v)} />
        <Field label="Nr ubezpieczenia społecznego" value={form.social_security_number} onChange={v => set('social_security_number', v)} />
      </div>
      <Field label="Adres zamieszkania" value={form.full_address} onChange={v => set('full_address', v)} />

      {/* Section 2+3: Status */}
      <SectionTitle num={2} title="STATUS I PODSTAWA PRAWNA" />
      <div style={row2}>
        <div style={fieldGroup}>
          <label style={labelStyle}>Status ubezpieczonego</label>
          <select value={form.employment_status} onChange={e => set('employment_status', e.target.value)} style={inputStyle}>
            <option value="employed">Pracownik najemny</option>
            <option value="self_employed">Działalność własna</option>
          </select>
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>Podstawa prawna delegowania</label>
          <select value={form.posting_type} onChange={e => set('posting_type', e.target.value)} style={inputStyle}>
            <option value="art12">Art. 12 — delegowanie</option>
            <option value="art13">Art. 13 — praca w 2+ państwach</option>
          </select>
        </div>
      </div>
      <div style={row2}>
        <Checkbox label="Poprzednie zaświadczenie A1" checked={form.previous_a1} onChange={v => set('previous_a1', v)} />
        <Checkbox label="Zastępuje innego pracownika" checked={form.replaces_worker} onChange={v => set('replaces_worker', v)} />
      </div>

      {/* Section 4: Employer */}
      <SectionTitle num={4} title="DANE PRACODAWCY (PL)" />
      <div style={{
        padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 13,
        background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)',
      }}>
        <div><strong>{currentCompany?.name}</strong></div>
        <div style={{ opacity: 0.7 }}>NIP: {currentCompany?.nip} | REGON: {currentCompany?.regon || '—'}</div>
        <div style={{ opacity: 0.7 }}>{currentCompany?.address}</div>
      </div>
      <Field label="NKP ZUS pracodawcy" value={form.employer_zus_nkp} onChange={v => set('employer_zus_nkp', v)} placeholder="Numer płatnika ZUS" />

      {/* Section 5: Destination */}
      <SectionTitle num={5} title="PAŃSTWO I PRZEDSIĘBIORSTWO DOCELOWE" />
      <div style={fieldGroup}>
        <label style={labelStyle}>Państwo wykonywania pracy</label>
        <select value={form.destination_country} onChange={e => set('destination_country', e.target.value)} style={inputStyle}>
          <option value="">— Wybierz kraj —</option>
          {EU_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
        </select>
      </div>
      <Field label="Nazwa przedsiębiorstwa za granicą" value={form.destination_company_name} onChange={v => set('destination_company_name', v)} />
      <Field label="Adres przedsiębiorstwa za granicą" value={form.destination_company_address} onChange={v => set('destination_company_address', v)} />

      {/* Section 6: Period */}
      <SectionTitle num={6} title="OKRES DELEGOWANIA" />
      <div style={row2}>
        <Field label="Data rozpoczęcia" type="date" value={form.posting_start_date} onChange={v => set('posting_start_date', v)} />
        <Field label="Data zakończenia" type="date" value={form.posting_end_date} onChange={v => set('posting_end_date', v)} />
      </div>
      <Field label="Rodzaj wykonywanej pracy" value={form.type_of_work} onChange={v => set('type_of_work', v)} placeholder="np. prace budowlane, montażowe..." />

      <div style={fieldGroup}>
        <label style={labelStyle}>Uwagi</label>
        <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)}
          style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button onClick={save} disabled={saving} style={{
          flex: 1, padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 15,
          opacity: saving ? 0.6 : 1,
        }}>{saving ? 'Zapisywanie...' : (editId ? 'Zapisz zmiany' : 'Utwórz wniosek')}</button>
        <button onClick={() => setView('list')} style={{
          padding: '14px 24px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
          background: 'transparent', color: '#fff', cursor: 'pointer',
        }}>Anuluj</button>
      </div>
    </div>
  );
}

function SectionTitle({ num, title }) {
  return (
    <div style={{
      padding: '8px 14px', borderRadius: 10, marginBottom: 14, marginTop: 20,
      background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(99,102,241,0.05))',
      borderLeft: '4px solid #6366f1', fontWeight: 700, fontSize: 13,
    }}>Sekcja {num}: {title}</div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div style={fieldGroup}>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={inputStyle} />
    </div>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: '#6366f1' }} />
      <label style={{ fontSize: 13, opacity: 0.8 }}>{label}</label>
    </div>
  );
}

const btnSmall = {
  padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer', fontSize: 12,
};
